import { useEffect, useMemo, useRef, useState } from "react";
import { api, API, fmtPeso, fmtDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UploadCloud, Loader2, Trash2, FileText, FileCode, Download, X } from "lucide-react";

export default function MexicanaFacturas() {
  const { can } = useAuth();
  const canEdit = can("aduanas", "edit");
  const canDelete = can("aduanas", "delete");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newXml, setNewXml] = useState(null);
  const [newPdf, setNewPdf] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [pdfView, setPdfView] = useState(null);
  const [xmlView, setXmlView] = useState(null);
  const xmlRef = useRef();
  const pdfRef = useRef();
  const token = localStorage.getItem("token");

  const load = () => {
    setLoading(true);
    api.get(`/aduanas/mexicana/facturas`)
      .then((r) => { setRows(r.data); setSelected(new Set()); })
      .catch(() => toast.error("No se pudieron cargar las facturas"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!newXml || !newPdf) { toast.error("Selecciona el XML y el PDF"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("xml_file", newXml);
      fd.append("pdf_file", newPdf);
      await api.post("/aduanas/mexicana/facturas", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Factura cargada");
      setNewXml(null); setNewPdf(null);
      if (xmlRef.current) xmlRef.current.value = "";
      if (pdfRef.current) pdfRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar factura?")) return;
    await api.delete(`/aduanas/mexicana/facturas/${id}`);
    toast.success("Factura eliminada");
    load();
  };

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allChecked = rows.length > 0 && selected.size === rows.length;
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(rows.map((r) => r.id)));

  const exportZip = async () => {
    if (selected.size === 0) { toast.error("Selecciona al menos una factura"); return; }
    try {
      const res = await api.post("/aduanas/mexicana/facturas/export-zip",
        { ids: Array.from(selected), mode: "both" }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "facturas_agencia_mexicana.zip";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo generar el ZIP");
    }
  };

  const fileUrl = useMemo(() => (id, kind) => `${API}/aduanas/mexicana/facturas/file/${id}/${kind}?auth=${token}`, [token]);

  const input = "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900";

  return (
    <div className="space-y-5" data-testid="mex-facturas-tab">
      {/* Carga */}
      {canEdit && (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-4">
          <h3 className="font-heading font-semibold text-slate-900 mb-3">Cargar factura (XML CFDI + PDF)</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <label className="text-xs text-slate-500">Archivo XML</label>
              <input ref={xmlRef} data-testid="mex-fac-xml" type="file" accept=".xml,text/xml,application/xml" className={input}
                onChange={(e) => setNewXml(e.target.files?.[0] || null)} />
            </div>
            <div>
              <label className="text-xs text-slate-500">Archivo PDF</label>
              <input ref={pdfRef} data-testid="mex-fac-pdf" type="file" accept=".pdf,application/pdf" className={input}
                onChange={(e) => setNewPdf(e.target.files?.[0] || null)} />
            </div>
            <button data-testid="mex-fac-upload" onClick={upload} disabled={uploading}
              className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-800 flex items-center justify-center gap-2 disabled:opacity-50">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Subir factura
            </button>
          </div>
        </div>
      )}

      {/* Acciones tabla */}
      <div className="flex items-center justify-end">
        {selected.size > 0 && (
          <button data-testid="mex-fac-zip" onClick={exportZip}
            className="bg-emerald-600 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-emerald-700 flex items-center gap-2">
            <Download className="h-4 w-4" /> Descargar ZIP ({selected.size})
          </button>
        )}
      </div>

      {/* Tabla */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead className="bg-slate-50 text-slate-500 border-y border-slate-200">
            <tr>
              <th className="px-3 py-3 w-10"><input data-testid="mex-fac-select-all" type="checkbox" checked={allChecked} onChange={toggleAll} className="rounded border-slate-300" /></th>
              <th className="px-3 py-3 font-medium text-left">Folio</th>
              <th className="px-3 py-3 font-medium text-left">Emisor</th>
              <th className="px-3 py-3 font-medium text-left">RFC Emisor</th>
              <th className="px-3 py-3 font-medium text-left">Fecha</th>
              <th className="px-3 py-3 font-medium text-right">Total</th>
              <th className="px-3 py-3 font-medium text-left">Archivos</th>
              <th className="px-3 py-3" />
            </tr>
          </thead>
          <tbody data-testid="mex-fac-table">
            {loading && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">Sin facturas cargadas</td></tr>}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                <td className="px-3 py-2.5"><input data-testid={`mex-fac-select-${r.id}`} type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} className="rounded border-slate-300" /></td>
                <td className="px-3 py-2.5 text-slate-900 font-medium">{r.folio || "—"}</td>
                <td className="px-3 py-2.5 text-slate-600">{r.emisor_nombre || "—"}</td>
                <td className="px-3 py-2.5 text-slate-600">{r.emisor_rfc || "—"}</td>
                <td className="px-3 py-2.5 text-slate-600">{fmtDate(r.fecha)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{fmtPeso(r.total)}</td>
                <td className="px-3 py-2.5">
                  <button data-testid={`mex-fac-view-pdf-${r.id}`} onClick={() => setPdfView(r)} className="inline-flex items-center text-red-500 hover:text-red-700 mr-3" title="Ver PDF"><FileText className="h-4 w-4" /></button>
                  <button data-testid={`mex-fac-view-xml-${r.id}`} onClick={() => setXmlView(r)} className="inline-flex items-center text-blue-500 hover:text-blue-700" title="Ver XML"><FileCode className="h-4 w-4" /></button>
                </td>
                <td className="px-3 py-2.5 text-right">
                  {canDelete && <button data-testid={`mex-fac-del-${r.id}`} onClick={() => del(r.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Visor PDF */}
      {pdfView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => setPdfView(null)}>
          <div className="bg-white rounded-md shadow-lg w-full max-w-4xl h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="mex-fac-pdf-viewer">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><FileText className="h-4 w-4 text-red-600" /> {pdfView.folio || "Factura"} · PDF</h3>
              <button onClick={() => setPdfView(null)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <iframe title="pdf" src={fileUrl(pdfView.id, "pdf")} className="flex-1 w-full rounded-b-md" />
          </div>
        </div>
      )}

      {/* Visor XML */}
      {xmlView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => setXmlView(null)}>
          <div className="bg-white rounded-md shadow-lg w-full max-w-3xl h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="mex-fac-xml-viewer">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><FileCode className="h-4 w-4 text-blue-600" /> {xmlView.folio || "Factura"} · XML</h3>
              <button onClick={() => setXmlView(null)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <iframe title="xml" src={fileUrl(xmlView.id, "xml")} className="flex-1 w-full rounded-b-md" />
          </div>
        </div>
      )}
    </div>
  );
}
